import {observer} from 'mobx-react-lite'
import React from 'react'
import {Lang} from 'yougile-lang'
import {Hub} from 'yougile-model'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {Accordion} from '@components/common/base/accordion'
import {LinkButtonNew} from '@components/common/base/buttons/link-button-new'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {crmStore} from '@components/crm/crm-store'
import {
  getDefaultContactPersonFields,
  getDefaultOrganizationFields
} from '@components/crm/default-data-fields'
import {gotoProject} from '@components/main-menu/handlers/main-handlers'
import {useDealDetails} from '@components/right-panel/crm/about-client/use-deal-details'
import {UserName} from '@components/user-name'
import {PageService} from '@extensions/page-service'
import {UserStickerState} from '@extensions/sticker/views-web/user/user-sticker-state'
import {getBoardByTask, getProjectByTask} from '@extensions/task'
import {CustomFieldInput} from './custom-field-input'
import {LinkedHubWithFields} from './linked-hub-with-fields'

export const AboutClientPanel = observer(({deal}: {deal: Hub}) => {
  const {
    contactPerson,
    organization,
    managerUserId,
    availableUsers,
    onChooseManager
  } = useDealDetails(deal)

  const {project} = getProjectByTask(deal.id) ?? {}
  const contactPersons = crmStore.getProjectContactPersons(project)
  const organizations = crmStore.getProjectOrganizations(project)

  const onClickAddCustomFields = () => {
    const board = getBoardByTask(deal.id)
    if (board) {
      const currentProject = PageService.getCurrentProject()
      if (currentProject?.id !== project.id) {
        gotoProject(project.id)
      }
      boardSettingsStore.openForBoard(board.id, 'customFields')
      boardSettingsStore.addIntoAdditionalTabInfo({
        addDraftFieldOnInit: true
      })
    }
  }

  const items = [
    {
      id: 'customFieldsGroup',
      title: <strong>{Lang.msg.crm.boardSettings.customFields}</strong>,
      content: (() => {
        const customFields = project?.data.customFields || []

        return (
          <>
            {customFields.map(field => (
              <CustomFieldInput
                key={field.id}
                label={field.label}
                value={deal.data.fieldValues?.[field.id] ?? ''}
                onConfirm={nextValue => {
                  deal.data.fieldValues ??= {}
                  deal.data.fieldValues[field.id] = nextValue
                  deal.commit()
                }}
                type={field.type}
              />
            ))}
            <LinkButtonNew
              icon="IconPlus"
              title={Lang.msg.crm.boardSettings.addNewField}
              onClick={onClickAddCustomFields}
              className="pl-12 pt-8 mt-8 mb-20"
            />
          </>
        )
      })()
    },
    {
      id: 'contactPersonGroup',
      title: <strong>{Lang.msg.crm.rightPanel.contactPerson.contact}</strong>,
      content: (
        <LinkedHubWithFields
          hub={contactPerson}
          label={Lang.msg.crm.rightPanel.contactPerson.name}
          getAddAsNewLabel={Lang.msg.crm.newDealForm.addAsNewContactPersonLabel}
          suggestions={contactPersons.map(org => ({
            id: org.id,
            title: org.title
          }))}
          fields={getDefaultContactPersonFields()}
          onLink={link => {
            const person = link.id
              ? contactPersons.find(c => c.id === link.id)
              : link.value

            crmStore.linkContactPerson(deal, person)
          }}
          onClickUnlink={() => {
            crmStore.unlinkContactPerson(deal.id)
          }}
        />
      )
    },
    {
      id: 'organizationGroup',
      title: <strong>{Lang.msg.crm.rightPanel.organization.title}</strong>,
      content: (
        <LinkedHubWithFields
          hub={organization}
          label={Lang.msg.crm.rightPanel.organization.name}
          getAddAsNewLabel={Lang.msg.crm.newDealForm.addAsNewOrganizationLabel}
          suggestions={organizations.map(org => ({
            id: org.id,
            title: org.title
          }))}
          fields={getDefaultOrganizationFields()}
          onLink={link => {
            const org = link.id
              ? organizations.find(c => c.id === link.id)
              : link.value

            crmStore.linkOrganization(deal, org)
          }}
          onClickUnlink={() => {
            crmStore.unlinkOrganization(deal.id)
          }}
        />
      )
    }
  ]

  return (
    <div className="flex flex-col h-full min-h-0">
      <WithPerfectScrollbar
        className="flex-1 min-h-0"
        options={{suppressScrollX: true}}
      >
        <form onSubmit={e => e.preventDefault()} noValidate>
          <div className="px-24 py-16 space-y-8 text-14">
            <div className="grid grid-cols-[150px_1fr] py-2 px-12 text-14 items-center mb-16">
              <div className="text-placeholder">
                {Lang.msg.crm.rightPanel.contactPerson.manager}
              </div>
              <div className="flex items-center w-full gap-6">
                <UserStickerState
                  users={managerUserId ? [managerUserId] : []}
                  projectId={deal.location}
                  options={availableUsers}
                  onChoose={onChooseManager}
                  singleMode
                  withButton
                />
                <div>
                  <UserName userId={managerUserId} />
                </div>
              </div>
            </div>

            <Accordion
              multi
              defaultOpenedIds={[
                'customFieldsGroup',
                'contactPersonGroup',
                'organizationGroup'
              ]}
              headerStyle="filled"
              items={items}
            />
          </div>
        </form>
      </WithPerfectScrollbar>
    </div>
  )
})
